import { Context } from "./Provider";
import { useState, useContext, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  sanitizePostal,
  buildJobURL,
  useRemovePageQSFromURL,
  scrollToTarget,
} from "../helpers/helpers.js";
import JobCard from "./JobCard";
import JobCardSkeleton from "./JobCardSkeleton";
import Pagination from "./Pagination";
import JobResultsInfo from "./JobResultsInfo";

const JobResults = () => {
  // State to handle window width for responsive behavior
  const [width, setWidth] = useState(window.innerWidth),
    breakpoint = 768, // Mobile breakpoint
    breakpoint2 = 992; // Tablet breakpoint
    
  const [showLeftLinks, setShowLeftLinks] = useState(true);

  const {
    updateJobDetail, // Function to update job detail view
    data, // Job data fetched from the API
    loading,  // Flag to help show skeleton card gtw 121025 addition
    updateJobParams, // Function to update job parameters like pagination
    pageIndex, // Current page index for pagination
    updatePageIndex, // Function to update the page index
  } = useContext(Context);

  const [jobDetailWasClicked, setJobDetailWasClicked] = useState(false);
  const [activeCardID, setActiveCardID] = useState(null); // ID of the currently active job card
  const removePageQSFromURL = useRemovePageQSFromURL();
  const [paginationParamLoaded, setPaginationParamLoaded] = useState(false); //checks if page QS loads to ensure it only runs once and avoids a loop
  const [searchParams, setSearchParams] = useSearchParams();
  // Retrieve 'pg' parameter from URL, or set to 1 if not found
  const paginationParam = searchParams.get("pg")
    ? parseInt(searchParams.get("pg"))
    : null;
  const resultsPerPage = 20;
  const dataIndex = Math.ceil(pageIndex / (1000 / resultsPerPage)); //grouping of results i.e., first 1k=1, second 1k=2, etc up to 10 (10k results)
  const resultsOffsetFirst = pageIndex - (dataIndex - 1) * 50; //sets the offset num of results i.e., pageIndex3 - (dataIndex3-1) * 100
  const resultsOffset = (resultsOffsetFirst - 1) * resultsPerPage; // the final offset i.e., page 1 === (1-1) * 10 = 0, page 2 === (2-1) * 10 = 10, page 3 === (3-1) * 10 = 20,
  // Define the memoized function here, in the parent component
  const updateShowFilter = useCallback((e) => {
    if (e) e.preventDefault();
    document.body.classList.toggle("job-filter-active");
  }, []);
  // New functions to manage the state
  const handleShowLeftLinks = () => {
    setShowLeftLinks(true);
  };
  const handleHideLeftLinks = () => {
    setShowLeftLinks(false);
  };

  /**
   * Handles page clicks for pagination, updating the job parameters and scroll position.
   */
  const pageClick = (e, selectedPageNum) => {
    // Prevent default action and remove query string from URL if triggered by an event
    if (e) {
      e.preventDefault();
      removePageQSFromURL();
    }

    // Calculate the index of the clicked page
    const dataIndexOfClickedPage = Math.ceil(
      selectedPageNum / (1000 / resultsPerPage)
    );

    // If the clicked page is on a different data index (e.g., outside the first 1k results)
    if (dataIndex !== dataIndexOfClickedPage) {
      if (dataIndexOfClickedPage <= 10) {
        updateJobParams({ from: (dataIndexOfClickedPage - 1) * 1000 });
      } else {
        if (
          dataIndexOfClickedPage === dataIndex + 1 &&
          data?.hits?.hits?.length > 0
        ) {
          const sort_id = data.hits.hits.at(-1).sort[0]; // Get the sort key from the last hit
          updateJobParams({ from: 0, sort_id: sort_id });
        } else {
          const searchAftersNeeded = dataIndexOfClickedPage - 10;
          updateJobParams({ search_after_count: searchAftersNeeded });
        }
      }
    }

    // Ensure the detail view isn't clicked and update the page index
    setJobDetailWasClicked(false);
    updatePageIndex(selectedPageNum);

    // Scroll to the target (job results column) after the page change
    scrollToTarget("#job_results");
  };

  /**
   * Handles job card clicks, updating the job detail view and browser history for larger screens,
   * and redirecting to the job detail page for smaller screens.
   */
  const jobClick = (e, fields) => {
    e.preventDefault();

    if (!fields || !fields.job_order_id?.[0]) {
      console.error("Invalid or missing job data");
      return;
    }

    const getField = (key) => fields[key]?.[0] ?? "";

    const jobId = getField("job_order_id");
    const jobTitle = getField("job_title");
    const jobCategory = getField("job_category");
    const jobCity = getField("job_city");
    const jobState = getField("job_state_abbr");
    const jobPostal = sanitizePostal(getField("job_postal"));
    const jobCatId = getField("job_category_id");

    setActiveCardID(jobId);

    const thisURL = buildJobURL(
      jobCategory,
      jobCity,
      jobState,
      jobPostal,
      jobId,
      jobCatId
    );

    if (width >= breakpoint) {
      setJobDetailWasClicked(true);
      updateJobDetail(fields);
      scrollToTarget("#job_results");
    } else {
      window.location.href = thisURL;
    }
  };

  const addNoJobsBodyClass = () => {
    if (!document.body.classList.contains("no-jobs-found")) {
      document.body.classList.add("no-jobs-found");
    }
  };

  const removeNoJobsBodyClass = () => {
    if (document.body.classList.contains("no-jobs-found")) {
      document.body.classList.remove("no-jobs-found");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      // On desktop, always show the links; on mobile, hide them initially
      if (window.innerWidth >= breakpoint2) {
        setShowLeftLinks(true);
      } else {
        setShowLeftLinks(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call to set state on component mount

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Reset detail state on new search
    setJobDetailWasClicked(false);
  }, [data]);

  useEffect(() => {
    // Check if data and data.hits are defined and there are results
    if (data?.hits?.total?.value > 0 && Array.isArray(data.hits.hits)) {
      // Ensure resultsOffset is valid (defaults to 0 if undefined)
      const offset = resultsOffset || 0;
      const currentResultsSet = data.hits.hits.slice(offset);

      // Check if there are results in the current set
      if (currentResultsSet.length > 0) {
        // Set the activeCardID to the first job order ID in the current results
        const firstJobOrderID = currentResultsSet[0].fields.job_order_id[0];
        setActiveCardID(firstJobOrderID);
      }
    }
  }, [data, resultsOffset]); // Add resultsOffset to dependency array if it's dynamic

  useEffect(() => {
    if (!jobDetailWasClicked && window.innerWidth >= 769) {
      const defaultJob = data?.hits?.hits?.[resultsOffset]?.fields;
      if (defaultJob) {
        updateJobDetail(defaultJob);
      }
    }
  }, [data, jobDetailWasClicked, resultsOffset]);


  // if (loading) {
  //   return (
  //     <div className="jobs-loading">
  //       <LoadingBlock />
  //     </div>
  //   );
  // }

  // Handle pagination parameter and set the current page if needed
  const isPaginationParamValid =
    paginationParam !== null && !isNaN(paginationParam);
  if (isPaginationParamValid && !paginationParamLoaded) {
    setPaginationParamLoaded(true); // Prevent reprocessing
    pageClick(null, paginationParam); // Set the current page
  }

  // Check if there are valid job hits and the pagination parameter is within bounds
  const numResults = data?.hits?.total?.value;
  const numOfPages = Math.ceil(numResults / resultsPerPage);
  const isPaginationParamInBounds = paginationParam <= numOfPages;
  const hasJobs = data.hits.total.value > 0;

  if (hasJobs && isPaginationParamInBounds) {
    // Show job detail section if it exists
    const jobDetailElement = document.getElementById("job-detail");
    if (jobDetailElement) {
      jobDetailElement.style.display = "block";
    }

    // Remove 'no jobs found' body class if it exists
    removeNoJobsBodyClass();

    return (
      <div className="job-results-column">
        {/* <div className="job-results-info-header">
          <h2>Job Results</h2>
        </div>
         */}
        {/* <JobResultsInfo /> */}
         <JobResultsInfo width={width} updateShowFilter={updateShowFilter} />
        <ul
          id="job_results__list"
          className="job-list"
          aria-label="Listing of available jobs"
        >
          {loading
            ? Array.from({ length: resultsPerPage }).map((_, i) => (
                <JobCardSkeleton key={i} />
              ))
            : data.hits.hits
                .slice(resultsOffset, resultsOffset + resultsPerPage)
                .map(row => (
                  <JobCard
                    key={row._id}
                    job={row}
                    isActive={activeCardID === row.fields.job_order_id[0]}
                    onClick={(e) => jobClick(e, row.fields)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        jobClick(e, row.fields);
                      }
                    }}
                  />
                ))}
        </ul>
        {/* Call the Pagination component and pass the required props */}
        <Pagination
          numResults={numResults}
          resultsPerPage={resultsPerPage}
          currentPage={pageIndex}
          onPageChange={pageClick}
        />
        <p>
          <em>
            An Equal Opportunity Employer. All qualified applicants will receive
            consideration for employment without regard to race, color,
            religion, sex, sexual orientation, gender identity, national origin,
            or protected veteran status and will not be discriminated against on
            the basis of disability.
          </em>
        </p>
      </div>
    );
  } else {
    // Hide job detail section if no jobs are found
    const jobDetailElement = document.getElementById("job-detail");
        if (jobDetailElement) {
            jobDetailElement.style.display = "none";
        }

    // Add 'no jobs found' body class, hide detail column in component
    addNoJobsBodyClass();
    return (
      <div className="col-md-8">
        <div className="no-jobs-found-block">
          <h3>No Jobs Found</h3>
         <p>There are currently no jobs on this page or any that matched your search criteria.</p>
          <p>Please change your search criteria, <a href="/pharmacy-jobs/">search other types of jobs</a> or <a href="/send-resume/" title="Send your resume">send your resume by completing our application here</a>.</p>
        </div>
      </div>
    );
  }
};

export default JobResults;